import { useMemo, useState } from "react";
import { SxProps, Theme } from "@mui/material/styles";

// Hover focus of one UH cat in the multi-cat charts (booking curve, price):
// the cat under the pointer stays opaque, the others fade — in both charts at
// once, since the focus is held by AnalyseTab.
//
// The pointer cat is resolved at chart level from the element under the
// pointer rather than with enter/leave handlers per element: recharts unmounts
// active dots as the cursor moves, and an element removed under the pointer
// never fires mouseleave, which left a cat stuck focused. The fade itself is
// pure CSS on the wrapper (`catFocusSx`), so a focus change never touches a
// recharts child prop — that would make recharts rebuild every series.

export const FADED_OPACITY = 0.15;

const CAT_CLASS_PREFIX = "chart-cat-";
const CAT_CLASS_PATTERN = /chart-cat-(\d+)/;

export const catClassName = (catId: number) => `${CAT_CLASS_PREFIX}${catId}`;

export const catIdFromEventTarget = (
  target: EventTarget | null
): number | null => {
  if (!(target instanceof Element)) return null;
  const el = target.closest(`[class*="${CAT_CLASS_PREFIX}"]`);
  const match = el?.getAttribute("class")?.match(CAT_CLASS_PATTERN);
  return match?.[1] != null ? Number(match[1]) : null;
};

export interface CatFocus {
  pointerCatId: number | null;
  setPointerCatId: (catId: number | null) => void;
}

export const useCatFocus = (): CatFocus => {
  const [pointerCatId, setPointerCatId] = useState<number | null>(null);
  return useMemo(() => ({ pointerCatId, setPointerCatId }), [pointerCatId]);
};

// Only a rendered cat focuses: the pointer cat may have been unchecked, or
// belong to the other chart's selection, since the pointer entered it.
export const resolveFocusedCat = (
  pointerCatId: number | null,
  renderedCatIds: number[]
): number | null =>
  pointerCatId != null && renderedCatIds.includes(pointerCatId)
    ? pointerCatId
    : null;

// Bands (corridor) fade with their cat but are not hover targets: the pointer
// goes through them to the curves and dots.
const BASE_SX: SxProps<Theme> = {
  "& .recharts-area": { pointerEvents: "none" },
};

export const catFocusSx = (focusedCatId: number | null): SxProps<Theme> =>
  focusedCatId == null
    ? BASE_SX
    : {
        ...BASE_SX,
        [`& [class*="${CAT_CLASS_PREFIX}"]`]: { opacity: FADED_OPACITY },
        [`& .${catClassName(focusedCatId)}`]: { opacity: 1 },
      };
